import React, {useCallback, useEffect} from 'react';
import {Autocomplete, TextField, Typography} from '@mui/material';
import {Season} from "../../schemas/seasons/seasonsSchema";
import useSWR from "swr";
import {getFetcher} from "../../utils/api/fetchers";
import useQueryParams from "../../utils/hooks/useQueryParams";

interface SeasonAutocompleteProps {
    selectedSeason?: Season | null;
    onSeasonChange: (season: Season | null) => void;
}

const SeasonAutocomplete: React.FC<SeasonAutocompleteProps> = ({selectedSeason, onSeasonChange}) => {
    const {data: seasons, isLoading, error} = useSWR<Season[]>('/seasons/all', getFetcher);
    const {getQueryParam, setQueryParam, removeQueryParam} = useQueryParams();

    const getYearFromUrl = useCallback(() => {
        const year = getQueryParam('year');
        return year ? parseInt(year, 10) : null;
    }, [getQueryParam]);

    useEffect(() => {
        if (seasons) {
            const yearFromUrl = getYearFromUrl();
            if (yearFromUrl) {
                const found = seasons.find(season => season.year === yearFromUrl);
                if (found && (!selectedSeason || selectedSeason.id !== found.id)) {
                    onSeasonChange(found);
                    return;
                }
            }

            // Select the season with the highest year by default
            if (selectedSeason === null) {
                const sortedSeasons = [...seasons].sort((a, b) => b.year - a.year);
                onSeasonChange(sortedSeasons[0]);
            }
        }
    }, [seasons, onSeasonChange, selectedSeason, getYearFromUrl]);

    const handleSeasonChange = (value: Season | null) => {
        onSeasonChange(value);
        if (value) {
            setQueryParam('year', value.year.toString());
        } else {
            removeQueryParam('year');
        }
    };

    if (isLoading || !seasons) return <Typography>Načítání...</Typography>;
    if (error) return <Typography>Chyba načítání...</Typography>;

    const sortedSeasons = [...seasons].sort((a, b) => b.year - a.year);

    return (
        <Autocomplete
            options={sortedSeasons}
            value={selectedSeason}
            getOptionLabel={(option) => option.year.toString()}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Výběr sezóny" variant="standard"/>}
            onChange={(_, value) => handleSeasonChange(value)}
        />
    );
};

export default SeasonAutocomplete;